/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation, Theme } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import type { ButtonProps } from '@coursera/cds-core';
import { Button, Chip, ChipGroup, Grid, PageGridContainer, Typography, Typography2 } from '@coursera/cds-core';
import { ArrowUpIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { Spacer } from 'bundles/enterprise-ui/components';
import { useEventingMetadata } from 'bundles/unified-career-academy/contexts/EventingMetadataContext';
import { getDomainsCountObj } from 'bundles/unified-career-academy/utils/helpers';

import _t from 'i18n!nls/career-academy';

export const DEFAULT_NUM_CARDS_TO_SHOW = 12;

export type CareersModuleProps = {
  title?: string | null;
  subtitle?: string | null;
  footer?: React.ReactNode | null;
  careerCardsWithRoles?: Array<{ role?: string | null; card?: React.ReactNode }>;
  cardCount?: number;
  showDomainFilter?: boolean;
  orgName?: string;
};

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);
const TrackedChip = withSingleTracked({ type: 'BUTTON' })(Chip);

const getAllCareersText = () => {
  return _t('All Careers');
};

const style: Record<string, Interpolation<Theme>> = {
  wrapper: () => css`
    padding-top: var(--cds-spacing-600);
  `,
  titles: () => css`
    padding-top: var(--cds-spacing-600);
    text-align: center;
  `,
  headingTitle: css`
    margin-bottom: 2px;
  `,
  filterText: () => css`
    margin-bottom: var(--cds-spacing-150);
  `,
  filter: () => css`
    padding-top: var(--cds-spacing-300);
  `,

  cardsSection: () => css`
    padding-top: var(--cds-spacing-600);
  `,
  roleCategoriesFilterSection: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 1.25rem;
  `,
  filterChipsSection: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  chip: () => css`
    && {
      &[aria-checked='true'] {
        background: var(--cds-color-neutral-primary);
      }
    }
  `,
  footer: () => css`
    max-width: 835px;
    margin-top: var(--cds-spacing-600);
    margin-bottom: var(--cds-spacing-1400);
    text-align: center;
  `,
  footerCtaWrapper: () => css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

const CareersModule = ({
  title,
  subtitle,
  footer,
  careerCardsWithRoles,
  cardCount = DEFAULT_NUM_CARDS_TO_SHOW,
  showDomainFilter,
  orgName,
}: CareersModuleProps) => {
  const [selectedDomain, setSelectedDomain] = useState<string | null>(getAllCareersText());
  const [isCareerCardListExpanded, setIsCareerCardListExpanded] = useState<boolean>(false);
  const careerListContainerRef = useRef<HTMLDivElement>(null);
  const eventingMetadata = useEventingMetadata();
  const allCareersChipFilterRef = React.useRef<HTMLButtonElement>(null);

  const onSelectAllDomainsClick = () => setSelectedDomain(getAllCareersText());

  const onExpandBtnClick = () => {
    setIsCareerCardListExpanded(true);
  };

  const onBackToTopClick = () => {
    if (careerListContainerRef) {
      careerListContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }

    if (allCareersChipFilterRef) {
      allCareersChipFilterRef.current?.focus();
    }
  };

  const onDomainClick = (domain: string) => {
    setSelectedDomain((prevSelectedDomain) => {
      if (domain === prevSelectedDomain) {
        return getAllCareersText();
      } else {
        return domain;
      }
    });
  };

  const getFormattedDomainString = (d: string, c: number) => {
    return `${d} (${c})`;
  };

  const getChipElementsArray = () => {
    const rolesLength = careerCardsWithRoles?.length ?? 0;
    const TrackedAllCareersFilterChip = withSingleTracked({ type: 'BUTTON' })(() => (
      <Chip
        role="button"
        onClick={onSelectAllDomainsClick}
        selected={selectedDomain === getAllCareersText()}
        key="All Careers"
        tabIndex={0}
        data-testid="filter-chip"
        css={style.chip}
        ref={allCareersChipFilterRef}
      >
        {getFormattedDomainString(getAllCareersText(), rolesLength)}
      </Chip>
    ));

    const chipElementsArray = [
      <TrackedAllCareersFilterChip trackingName="role_category_filter" trackingData={{ type: 'FILTER_ALL' }}>
        {getFormattedDomainString(getAllCareersText(), rolesLength)}
      </TrackedAllCareersFilterChip>,
    ];

    Object.entries(getDomainsCountObj(careerCardsWithRoles))
      .sort((a, b) => {
        if (a[1] > b[1]) {
          return -1;
        } else if (a[1] < b[1]) {
          return 1;
        } else {
          return 0;
        }
      })
      .forEach(([domain, count]) => {
        chipElementsArray.push(
          <TrackedChip
            onClick={() => onDomainClick(domain)}
            selected={selectedDomain === domain}
            key={domain}
            trackingName="role_category_filter"
            trackingData={{
              type: `FILTER_${selectedDomain === domain ? 'SELECT' : 'DESELECT'}`,
              domain,
              ...eventingMetadata,
            }}
            tabIndex={0}
            data-testid="filter-chip"
          >
            {getFormattedDomainString(domain, count)}
          </TrackedChip>
        );
      });

    return chipElementsArray;
  };

  const filteredRoles =
    selectedDomain === getAllCareersText()
      ? careerCardsWithRoles
      : careerCardsWithRoles?.filter((card) => card?.role && selectedDomain === card?.role);

  const cardsToShow = isCareerCardListExpanded
    ? filteredRoles?.map((role) => role.card)
    : filteredRoles?.map((role) => role.card)?.slice(0, cardCount);

  const numCareerCardsLeftToShow = (filteredRoles?.length ?? 0) - (filteredRoles?.slice(0, cardCount).length ?? 0);
  const hasOnlyOneRole = careerCardsWithRoles?.length === 1;

  const getSectionTitle = () => {
    if (hasOnlyOneRole && orgName) {
      return (
        <React.Fragment>
          <Typography variant="d2semibold" component="h2" css={style.headingTitle}>
            {_t('Recommended by #{orgName}', { orgName })}
          </Typography>
          <Typography variant="h2" component="p">
            {_t('Get on the fast-track to a successful career')}
          </Typography>
          <Spacer size={48} />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <Typography variant="d2semibold" component="h2" css={style.headingTitle}>
          {title}
        </Typography>
        <Typography variant="h2" component="p">
          {subtitle}
        </Typography>
      </React.Fragment>
    );
  };

  const getCardsSection = () => {
    if (hasOnlyOneRole) {
      return (
        <Grid item container css={style.cardsSection} justifyContent="center">
          {cardsToShow?.[0]}
        </Grid>
      );
    }

    return (
      <Grid item container spacing={32} css={style.cardsSection}>
        {cardsToShow?.map((card) => (
          <Grid item container xs={12} sm={6} md={4} justifyContent="center">
            {card}
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <section>
      <PageGridContainer>
        <Grid item container ref={careerListContainerRef} id="explore-careers" justifyContent="center">
          <Grid item css={style.titles} xs={12}>
            {getSectionTitle()}
          </Grid>
          {showDomainFilter && careerCardsWithRoles?.length && (
            <div css={style.roleCategoriesFilterSection}>
              <Typography2 component="h3" variant="subtitleMedium" css={style.filterText}>
                {_t('Filter by job category')}
              </Typography2>
              <div css={style.filterChipsSection}>
                <ChipGroup label="" aria-label={_t('Filter by job category')}>
                  {getChipElementsArray()}
                </ChipGroup>
              </div>
            </div>
          )}
          {getCardsSection()}
        </Grid>
        <Grid item container justifyContent="center" alignItems="center">
          <Grid container justifyContent="center" alignItems="center" css={style.footer}>
            <Grid item css={style.footerCtaWrapper}>
              {!isCareerCardListExpanded && numCareerCardsLeftToShow > 0 && (
                <TrackedButton
                  {...Button.defaultProps}
                  onClick={onExpandBtnClick}
                  trackingName="view_all_career_cards"
                  trackingData={eventingMetadata}
                >
                  {_t('View all (#{numCareerCardsLeftToShow} more)', { numCareerCardsLeftToShow })}
                </TrackedButton>
              )}
              {isCareerCardListExpanded && numCareerCardsLeftToShow > 0 && (
                <TrackedButton
                  {...Button.defaultProps}
                  onClick={onBackToTopClick}
                  icon={<ArrowUpIcon />}
                  iconPosition="before"
                  trackingName="back_to_top"
                  trackingData={eventingMetadata}
                >
                  {_t('Back to top')}
                </TrackedButton>
              )}
            </Grid>
            <Grid item>{footer}</Grid>
          </Grid>
        </Grid>
      </PageGridContainer>
    </section>
  );
};

export default CareersModule;
